import { MagicSignStatus, SignType } from './types';

// Re-export for callers that previously imported from constants.ts
export { deriveSigningLinks, buildUniqueUploadFileName, isReadyToGenerateLinks } from './utils';
// Shared primitives live in the leaf module (see signTypeConfig.ts for why) — re-exported here so
// existing callers can keep importing them from constants.ts.
export { SIGN_TYPE_LABELS, MAX_SIGNATORIES_DEFAULT } from './signTypeConfig';
import { SIGN_TYPE_LABELS } from './signTypeConfig';

export const MAGIC_SIGN_ENDPOINTS = {
    LIST: '/magic/sign/list',
    // Consolidated: replaces /config and /associateCompany
    UPDATE: '/magic/sign/update',
    GENERATE_LINKS: '/magic/sign/generateLinks',
    CANCEL: '/magic/sign/cancel',
    AUDIT: '/magic/sign/audit',
    DOWNLOAD_DOC: '/magic/sign/download-doc',
    CONFIG_META: '/magic/sign/config-meta',
    // Phase 2 endpoints — /users now handles both company and PNO users via optional orgId param
    USERS: '/magic/sign/users',
    // Consolidated: replaces /sendEmail and /sendReminder
    SEND_NOTIFICATION: '/magic/sign/sendNotification',
    // Phase 3 endpoints
    GLOBAL_LIST: '/magic/sign/global/list',
    /**
     * P2 consolidated intake endpoint (RECUR-26495).
     * Replaces both /upload and /sendForSigning.
     * Body: { mnemosyneDocId, fileName?, source? }
     * Doc must already be registered in DocStore via the MU pipeline.
     */
    CREATE: '/magic/sign/create',
} as const;

export const MAGIC_SIGN_PAGE_SIZE = 20;

/** Section header for the PNO's internal team in the signatory picker. */
export const MAGIC_SIGN_INTERNAL_TEAM_LABEL = 'Internal Team';

/** v1 policy: backend only accepts PDF. DOCX rejected server-side. */
export const MAGIC_SIGN_ALLOWED_TYPES = ['application/pdf'];
export const MAGIC_SIGN_ALLOWED_EXTENSIONS = ['.pdf'];
export const MAGIC_SIGN_PDF_ONLY_TOAST = 'Only PDF documents are supported for signing.';
export const MAGIC_SIGN_MAX_FILE_SIZE_MB = 10;

export const MAGIC_SIGN_STATUS_CONFIG: Record<MagicSignStatus, { label: string; color: string; bgColor: string }> = {
    [MagicSignStatus.UPLOADING]: { label: 'Uploading', color: '#555', bgColor: '#f5f5f5' },
    [MagicSignStatus.UPLOADED]: { label: 'Uploaded', color: '#6021B3', bgColor: '#ede9fe' },
    [MagicSignStatus.GENERATING_LINKS]: { label: 'Generating Links', color: '#555', bgColor: '#f5f5f5' },
    [MagicSignStatus.GENERATED_LINKS]: { label: 'Generated Links', color: '#6021B3', bgColor: '#ede9fe' },
    [MagicSignStatus.PARTIAL_SIGNED]: { label: 'Partial Signed', color: '#B26A00', bgColor: '#fff3e0' },
    [MagicSignStatus.ALL_SIGNED]: { label: 'All Signed', color: '#00AA00', bgColor: '#e2ffe9' },
    [MagicSignStatus.CANCELLED]: { label: 'Cancelled', color: '#FF6231', bgColor: '#ffebeb' },
} as const;

/**
 * The two user-facing sign-type choices. Aadhaar and DSC are merged into one "Aadhaar/DSC"
 * option that maps to AADHAAR internally (the Leegality flow handles both).
 */
export const SIGN_TYPE_OPTIONS: { id: SignType; label: string }[] = [
    { id: SignType.E_SIGN, label: SIGN_TYPE_LABELS[SignType.E_SIGN] },
    { id: SignType.AADHAAR, label: SIGN_TYPE_LABELS[SignType.AADHAAR] },
];

export const OTP_COOLDOWN_SECONDS = 60;
export const OTP_MAX_ATTEMPTS = 3;

/**
 * Mnemosyne upload flow ID for Magic Sign documents.
 * Value 16 is the platform-wide "general document upload" flow — same value
 * used by Magic Upload (useMnemosyneUpload.ts) and ComposeEmailModal.
 */
export const MAGIC_SIGN_UPLOAD_FLOW_ID = 16;

/**
 * Shared `source` discriminant stamped on MU-registration (/magic/update/docMetaData) and
 * Magic Sign create (/magic/sign/create) calls, for audit/tracing. One definition imported at
 * every call site (FileExplorer, MagicUploadFilesTable, useMagicSignLogic, useGlobalMagicSignLogic)
 * so a typo in one site can't silently mis-tag the source.
 */
export enum MagicSignSource {
    DATA_ROOM = 'DATA_ROOM',
    MAGIC_UPLOAD = 'MAGIC_UPLOAD',
    MAGIC_SIGN = 'MAGIC_SIGN',
}

/**
 * Per-event pill colours for the audit log timeline (Figma): Sent for Signing=blue,
 * Reminder=amber, Cancelled/Failed=red, Signed=green, everything else
 * (Uploaded, Associated Company)=neutral purple. Same {label→style} pattern as
 * MAGIC_SIGN_STATUS_CONFIG above, but keyed by a keyword match on the event's display
 * string (there is no stable event-type enum on AuditLogEvent to switch on instead).
 */
export function resolveAuditEventColour(event: string): { color: string; bg: string } {
    const e = (event || '').toLowerCase();
    switch (true) {
        case e.includes('cancel'):
            return { color: '#D32F2F', bg: '#FDECEA' };
        case e.includes('signed'):
            return { color: '#1F8B24', bg: '#E6F7E9' };
        case e.includes('reminder'):
            return { color: '#B26A00', bg: '#FFF3E0' };
        case e.includes('sent for signing'):
            return { color: '#1565C0', bg: '#E3F0FC' };
        case e.includes('fail'):
            return { color: '#D32F2F', bg: '#FDECEA' };
        default:
            return { color: '#6021B3', bg: '#F0EAFA' };
    }
}
